import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { LoadingService } from '@app/core'
import { NotificationService } from '@app/core/services/notification.service'
import { Observable } from 'rxjs'
import { RegisterData, RegisterQuery, RegisterService, RegisterSettings } from '@app/session/state'

type RegisterFormData = {
  email: FormControl<string>
  password: FormControl<string>
  confirmPassword: FormControl<string>
  firstName: FormControl<string>
  lastName: FormControl<string>
  company: FormControl<string>
  language: FormControl<string>
  country: FormControl<string>
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RegisterComponent implements OnInit {
  registerForm: FormGroup<RegisterFormData>

  isError = false

  isLoading$: Observable<boolean>

  settings$: Observable<RegisterSettings>

  constructor(
    private registerService: RegisterService,
    private registerQuery: RegisterQuery,
    private router: Router,
    private notificationService: NotificationService,
    loadingService: LoadingService,
    formBuilder: FormBuilder,
  ) {
    this.isLoading$ = loadingService.isLoading$
    this.settings$ = registerQuery.selectSettings$

    this.registerForm = formBuilder.group(
      {
        email: ['', Validators.email],
        password: ['', Validators.minLength(6)],
        confirmPassword: '',
        firstName: '',
        lastName: '',
        company: '',
        language: '',
        country: '',
      },
      { validators: this.passwordMatchValidator },
    )
  }

  ngOnInit(): void {
    if (!this.registerQuery.hasSettings()) {
      this.registerService.getSettings()
    }
  }

  get email(): AbstractControl {
    return this.registerForm.get('email')
  }

  get password(): AbstractControl {
    return this.registerForm.get('password')
  }

  get confirmPassword(): AbstractControl {
    return this.registerForm.get('confirmPassword')
  }

  get firstName(): AbstractControl {
    return this.registerForm.get('firstName')
  }

  get lastName(): AbstractControl {
    return this.registerForm.get('lastName')
  }

  get company(): AbstractControl {
    return this.registerForm.get('company')
  }

  get language(): AbstractControl {
    return this.registerForm.get('language')
  }

  get country(): AbstractControl {
    return this.registerForm.get('country')
  }

  setError(isError: boolean): void {
    this.isError = isError
  }

  onSubmit(): void {
    const formData = this.registerForm.value
    const data: RegisterData = {
      Email: formData.email,
      Password: formData.password,
      FirstName: formData.firstName,
      LastName: formData.lastName,
      Company: formData.company,
      LanguageCode: formData.language,
      CountryCode: formData.country,
    }
    this.setError(false)

    this.registerService.register(data).subscribe({
      next: () => {
        this.notificationService.success('UI.ConfirmEmail.Message.AccountCreated', null, { duration: 15000 })
        this.router.navigateByUrl('/login')
      },
      error: () => this.setError(true),
    })
  }

  onPasswordInput(): void {
    const { ...errors } = this.confirmPassword.errors || {}
    if (this.registerForm.hasError('passwordMismatch')) {
      this.confirmPassword.setErrors({ ...errors, passwordMismatch: true })
    } else {
      this.confirmPassword.setErrors({ ...errors })
      this.confirmPassword.updateValueAndValidity()
    }
  }

  passwordMatchValidator: ValidatorFn = (form: FormGroup<RegisterFormData>): ValidationErrors | null => {
    if (form.value.password === form.value.confirmPassword) {
      return null
    }
    return { passwordMismatch: true }
  }
}
